import React, { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import DropdownMenu from '@atlaskit/dropdown-menu';

import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';

type TriggerProps = ComponentPropsWithoutRef<typeof DropdownMenu>['trigger'];

type Props = {
	menuPosition: 'left' | 'right';
	renderActionButton: TriggerProps;
	isConfigPanelOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	children: ReactNode;
};

export const GraphConfigurationActionPanel = ({
	menuPosition,
	renderActionButton,
	children,
	isConfigPanelOpen,
	onOpenChange,
}: Props): React.JSX.Element => (
	<JSErrorBoundary
		id="object-graph-configuration-panel"
		packageName="jiraAssetsObjectGraph"
		fallback="unmount"
	>
		<DropdownContainer onClick={(e: React.SyntheticEvent) => e.stopPropagation()}>
			<DropdownMenu
				isOpen={isConfigPanelOpen}
				onOpenChange={(args) => {
					onOpenChange(args.isOpen);
				}}
				zIndex={1000}
				trigger={renderActionButton}
				shouldFlip={false}
				placement={`bottom-${menuPosition === 'left' ? 'start' : 'end'}`}
				appearance="tall"
			>
				<Box xcss={contentContainerStyles}>{children}</Box>
			</DropdownMenu>
		</DropdownContainer>
	</JSErrorBoundary>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownContainer = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"[data-role='droplistContent']": {
		overflow: 'auto',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"[role='menu']": {
		maxWidth: '400px',
	},
});

const contentContainerStyles = xcss({
	marginTop: 'space.200',
	marginRight: 'space.200',
	marginBottom: 'space.200',
	marginLeft: 'space.200',
	maxWidth: '370px',
});
